import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import { Users, FileText, DollarSign, Package, Download, Eye, Search, Filter } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const ManagerDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const [statsRes, clientsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/manager/stats`, { 
          headers 
        }),
        fetch(`${BACKEND_URL}/api/manager/clients`, { 
          headers 
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
        setFilteredClients(clientsData);
      } else {
        console.error('Failed to load clients:', clientsRes.status, await clientsRes.text());
      }
    } catch (error) {
      console.error('Error loading manager data:', error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  const filterClients = useCallback(() => {
    let filtered = clients;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.mobile.includes(searchTerm) ||
        client.client_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(client => client.total_paid_orders > 0);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(client => client.total_paid_orders === 0);
    }

    setFilteredClients(filtered);
  }, [clients, searchTerm, filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterClients();
  }, [filterClients]);

  const viewClientDetails = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(`${BACKEND_URL}/api/manager/clients/${clientId}`, {
        headers
      });
      if (res.ok) {
        const data = await res.json();
        setClientDetails(data);
        setSelectedClient(clientId);
      }
    } catch (error) {
      console.error('Error loading client details:', error);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/manager/documents/${documentId}/download`,
        { 
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please contact support.');
    }
  };

  const markOrderAsUnpaid = async (serviceRequestId) => {
    if (!window.confirm('Are you sure you want to mark this order as UNPAID? The user will be able to delete this service request.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/manager/orders/${serviceRequestId}/mark-unpaid`,
        {
          method: 'POST',
          headers
        }
      );

      if (response.ok) {
        alert('Order marked as unpaid successfully!');
        // Reload client details
        if (selectedClient) {
          viewClientDetails(selectedClient);
        }
        // Reload all clients data
        loadData();
      } else {
        throw new Error('Failed to mark as unpaid');
      }
    } catch (error) {
      console.error('Error marking order as unpaid:', error);
      alert('Failed to mark order as unpaid. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-600">Loading manager dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Manager Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage clients, orders, and documents</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Clients</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="text-blue-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Paid Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.paid_orders}</p>
                  <p className="text-xs text-gray-500 mt-1">{stats.pending_orders} pending</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <Package className="text-green-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Documents</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_documents}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <FileText className="text-purple-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-gray-900">₹{stats.total_revenue.toLocaleString()}</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg">
                  <DollarSign className="text-orange-600" size={28} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, mobile, or client ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" size={20} />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Clients</option>
                <option value="active">Active (Paid Orders)</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client ID
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid Orders
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500">{client.pan}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email}</div>
                      <div className="text-sm text-gray-500">{client.mobile}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900">{client.client_id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        client.total_paid_orders > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {client.total_paid_orders}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 text-purple-800">
                        {client.total_documents}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => viewClientDetails(client.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Eye size={16} />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredClients.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No clients found matching your search</p>
            </div>
          )}
        </div>
      </div>

      {/* Client Details Modal */}
      <Dialog open={selectedClient !== null} onOpenChange={() => {
        setSelectedClient(null);
        setClientDetails(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
            <DialogDescription>
              View client information, orders, and documents
            </DialogDescription>
          </DialogHeader>

          {clientDetails && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{clientDetails.user.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Client ID:</span>
                    <span className="ml-2 font-mono font-medium">{clientDetails.user.client_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2">{clientDetails.user.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Mobile:</span>
                    <span className="ml-2">{clientDetails.user.mobile}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">PAN:</span>
                    <span className="ml-2 font-mono">{clientDetails.user.pan}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">DOB:</span>
                    <span className="ml-2">{clientDetails.user.dob}</span>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{clientDetails.summary.total_orders}</p>
                  <p className="text-xs text-gray-600 mt-1">Total Orders</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{clientDetails.summary.paid_orders}</p>
                  <p className="text-xs text-gray-600 mt-1">Paid Orders</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{clientDetails.summary.total_documents}</p>
                  <p className="text-xs text-gray-600 mt-1">Documents</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{clientDetails.summary.total_payments}</p>
                  <p className="text-xs text-gray-600 mt-1">Payments</p>
                </div>
              </div>

              {/* Paid Orders with Documents */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Paid Orders & Documents</h3>
                {clientDetails.service_requests
                  .filter(order => order.payment_status === 'paid')
                  .map((order) => {
                    const orderDocs = clientDetails.documents.filter(
                      doc => doc.service_request_id === order.id
                    );
                    return (
                      <div key={order.id} className="border rounded-lg p-4 mb-3 bg-white shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{order.service_name || 'Service'}</h4>
                            <p className="text-sm text-gray-600">
                              Request ID: <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{order.id}</code>
                            </p>
                            <p className="text-sm text-gray-600">
                              Order Date: {new Date(order.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              Price: ₹{order.price || 'N/A'}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                              Paid
                            </span>
                            <button
                              onClick={() => markOrderAsUnpaid(order.id)}
                              className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold rounded transition-colors"
                              title="Mark as unpaid (allows user to delete)"
                            >
                              Mark as Unpaid
                            </button>
                          </div>
                        </div>

                        {orderDocs.length > 0 ? (
                          <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <FileText size={16} className="text-blue-600" />
                              Uploaded Documents ({orderDocs.length}):
                            </p>
                            {orderDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 hover:border-blue-300 transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <FileText className="text-blue-600" size={20} />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{doc.doc_type}</p>
                                    <p className="text-xs text-gray-500">
                                      Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                                    </p>
                                    {doc.password && (
                                      <div className="mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                        <span className="text-xs text-amber-800 font-medium">🔒 Password: </span>
                                        <code className="text-xs font-mono text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded">
                                          {doc.password}
                                        </code>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => downloadDocument(doc.id, doc.original_filename || doc.doc_type)}
                                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                                >
                                  <Download size={14} />
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded text-center">
                            <p className="text-sm text-gray-500 italic">No documents uploaded yet for this order</p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {clientDetails.service_requests.filter(o => o.payment_status === 'paid').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No paid orders</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerDashboardPage;
